import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { HardDrive, Loader2, CheckCircle2, XCircle, RefreshCw, Plug } from "lucide-react";
import { api, apiError } from "@/lib/api";

export default function AdminStorage() {
  const [st, setSt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [testing, setTesting] = useState(false);
  const [connecting, setConnecting] = useState(false);

  const load = () => { setLoading(true); api.get("/admin/storage").then((r) => setSt(r.data)).catch(() => {}).finally(() => setLoading(false)); };

  useEffect(() => {
    load();
    const p = new URLSearchParams(window.location.search);
    if (p.get("drive_connected")) { toast.success("Google Drive connected"); window.history.replaceState(null, "", "/admin"); }
    const err = p.get("drive_error");
    if (err) { toast.error(`Google Drive connection failed — ${decodeURIComponent(err)}`, { duration: 9000 }); window.history.replaceState(null, "", "/admin"); }
  }, []);

  const connect = async () => {
    setConnecting(true);
    try { const { data } = await api.get("/oauth/drive/connect"); window.location.href = data.authorization_url; }
    catch (e) { toast.error(apiError(e)); setConnecting(false); }
  };
  const test = async () => {
    setTesting(true);
    try { const { data } = await api.post("/admin/storage/test"); data.ok ? toast.success(data.message) : toast.error(data.message); load(); }
    catch (e) { toast.error(apiError(e)); } finally { setTesting(false); }
  };

  if (loading) return <div className="py-10 flex justify-center"><Loader2 className="animate-spin text-coral" /></div>;

  const connected = st?.connected;
  return (
    <div data-testid="admin-storage">
      <div className="bg-white rounded-2xl border border-border p-4">
        <div className="flex items-center gap-2 mb-3"><HardDrive className="text-coral" size={20} /><h2 className="font-display font-bold text-lg">Storage</h2></div>
        <div className="grid sm:grid-cols-2 gap-2">
          <Row k="Storage Provider" v="Google Drive" />
          <Row k="Connection" v={connected ? "Connected" : "Not Connected"} ok={connected} bad={!connected} />
          <Row k="Google Account" v={st?.account_email || "—"} />
          <Row k="Root Folder" v={st?.root_folder || "BiteGoFood"} />
          <Row k="Storage Health" v={st?.health || "—"} ok={st?.health === "Healthy"} bad={st?.health === "Error"} />
          <Row k="Files on Drive" v={st?.file_count ?? 0} />
        </div>
        <div className="flex gap-2 mt-4">
          <button onClick={connect} disabled={connecting} className="bg-coral text-white font-bold px-4 py-2.5 rounded-xl text-sm flex items-center gap-2" data-testid="drive-connect-btn">{connecting ? <Loader2 className="animate-spin" size={16} /> : <Plug size={16} />} {connected ? "Reconnect" : "Connect Google Drive"}</button>
          {connected && <button onClick={test} disabled={testing} className="bg-ink text-white font-bold px-4 py-2.5 rounded-xl text-sm flex items-center gap-2" data-testid="drive-test-btn">{testing ? <Loader2 className="animate-spin" size={16} /> : <RefreshCw size={16} />} Test Connection</button>}
        </div>
        {connected && st?.subfolders && (
          <div className="mt-4">
            <div className="text-xs font-bold text-ink-600 uppercase mb-1">Folder structure</div>
            <div className="flex flex-wrap gap-1.5">{["BiteGoFood", ...st.subfolders].map((f) => <span key={f} className="text-[11px] bg-surface px-2 py-1 rounded-lg text-ink">{f}</span>)}</div>
          </div>
        )}
        {!connected && <p className="text-xs text-ink-600 mt-3">Sensitive KYC documents are stored privately on the platform Google Drive and served only through authenticated BiteGoFood access. Until connected, uploads use the fallback object storage.</p>}
      </div>
    </div>
  );
}

function Row({ k, v, ok, bad }) {
  return (
    <div className="flex items-center justify-between bg-surface rounded-xl px-3 py-2.5">
      <span className="text-sm text-ink-600">{k}</span>
      <span className={`text-sm font-semibold flex items-center gap-1 ${ok ? "text-emerald-600" : bad ? "text-red-500" : "text-ink"}`}>
        {ok && <CheckCircle2 size={14} />}{bad && <XCircle size={14} />}{v}
      </span>
    </div>
  );
}
